import styled from "@emotion/styled"
import { PButton, PLink } from "@porsche-design-system/components-react"
import type { VinSearchResultView } from "@slfinrtl/ui-bff-routes"
import { addInventoryVehicleRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import { Link, useNavigate } from "react-router"
import { isUiErrorResponse } from "src/common/errorHandling/isUIErrorResponse/isUIErrorResponse"
import { formMessages } from "src/common/form.messages"
import { useDealerAppRoutes } from "src/common/routes/useDealerAppRoutes"
import { ButtonGroup, Input } from "src/components"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { Modal } from "src/context/modal/Modal"
import { ModalContext } from "src/context/modal/ModalContext"
import type { ModalProps } from "src/context/modal/ModalProvider"
import { useBffDynamic } from "src/hooks"
import { VinSearchResult } from "./components/vinSearchResult/VinSearchResult"
import { VinSearchResultSkeleton } from "./components/vinSearchResult/VinSearchResultSkeleton"
import { useVinSearch } from "./hooks/useVinSearch"

interface SearchVinFormValues {
	vin?: string
}

const messages = defineMessages({
	foundButton: {
		defaultMessage: "Add vehicle",
		id: "inventorylist.addVehicle.identified.addButtonLabel",
	},
	headline: {
		defaultMessage: "Add vehicle",
		id: "inventorylist.addVehicle.header",
	},
	inputLabel: {
		defaultMessage: "VIN (vehicle identification number)",
		id: "inventorylist.addVehicle.vinInputLabel",
	},
	manualButton: {
		defaultMessage: "Add vehicle manually",
		id: "inventorylist.addVehicle.unidentified.addButtonLabel",
	},
	missingVin: {
		defaultMessage: "Please enter a VIN",
		id: "inventorylist.addVehicle.missingVin",
	},
	viewButton: {
		defaultMessage: "View vehicle",
		id: "inventorylist.addVehicle.existing.viewButtonLabel",
	},
})

export const AddVehicleModalContainer = withErrorHandler(({ id }: ModalProps) => {
	const { formatMessage } = useIntl()
	const vinRef = useRef<HTMLInputElement>(null)

	const [isSearching, setIsSearching] = useState(false)
	const [isCreatingDraftVehicle, setIsCreatingDraftVehicle] = useState(false)

	const {
		clearErrors,
		formState,
		handleSubmit,
		register,
		setError: setFormError,
		setValue,
		watch,
	} = useForm<SearchVinFormValues>()

	const { hideModal } = useContext(ModalContext)
	const navigate = useNavigate()
	const routes = useDealerAppRoutes()

	const searchByVin = useVinSearch()
	const createDraftVehicle = useBffDynamic(addInventoryVehicleRoute, "inline")
	const [searchResult, setSearchResult] = useState<VinSearchResultView | null | undefined>()

	const vin = watch("vin")

	const hide = useCallback(() => hideModal(id), [hideModal, id])

	useEffect(() => {
		if (!isSearching) {
			setTimeout(() => vinRef.current?.focus(), 100)
		}
	}, [vinRef, isSearching])

	useEffect(() => {
		register("vin")
	}, [register])

	const search = useCallback(
		(searchVin: string) => {
			setIsSearching(true)
			searchByVin(searchVin.trim())
				.then((result) => setSearchResult(result))
				.catch((error?: unknown) => {
					if (isUiErrorResponse(error)) {
						error.response.data.validationErrors?.forEach((validationError) => {
							setFormError(validationError.key as keyof SearchVinFormValues, {
								message: validationError.message,
							})
						})
					}
				})
				.finally(() => setIsSearching(false))
		},

		[setFormError, searchByVin],
	)

	const create = useCallback(() => {
		if (!vin) {
			return
		}
		setIsCreatingDraftVehicle(true)
		createDraftVehicle({
			body: { identified: !!searchResult, vin: vin.trim() },
			pathParams: {},
			queryParams: {},
		})
			.then((result) => {
				if (routes?.inventoryList?.vehicleDetails?.add) {
					navigate(routes.inventoryList.vehicleDetails.add.url(result.inventoryId))
				}
				hide()
			})
			.finally(() => setIsCreatingDraftVehicle(false))
	}, [createDraftVehicle, navigate, hide, vin, searchResult, routes?.inventoryList?.vehicleDetails?.add])

	useEffect(() => {
		if (vin?.trim().length === 17) {
			search(vin)
		}
	}, [vin, search])

	const onSubmit = () => {
		if (!vin || vin.trim().length === 0) {
			setFormError("vin", {
				message: formatMessage(messages.missingVin),
			})
			return
		}

		search(vin)
	}

	const handleVinChanged = (value: string | undefined) => {
		clearErrors()
		setValue("vin", value?.toUpperCase().trim())
		if (searchResult !== undefined) {
			setSearchResult(undefined)
		}
	}

	return (
		<Modal id={id} heading={formatMessage(messages.headline)}>
			<ContentWrapper>
				<form onSubmit={handleSubmit(onSubmit)}>
					<Input
						name="vin"
						data-testid="add-vehicle-modal_vin-input"
						label={formatMessage(messages.inputLabel)}
						value={vin}
						errorMessage={formState.errors.vin?.message}
						onChange={handleVinChanged}
						disabled={isSearching}
						ref={vinRef}
						required
						type="search"
					/>
				</form>
				<ErrorNotificationInline />
				{isSearching && <VinSearchResultSkeleton />}
				{searchResult !== undefined && !isSearching && (
					<>
						<VinSearchResult view={searchResult} />

						<ButtonGroup data-testid="add-vehicle-modal_action-buttons">
							{searchResult === null && (
								<PButton loading={isCreatingDraftVehicle} onClick={create}>
									<FormattedMessage {...messages.manualButton} />
								</PButton>
							)}
							{searchResult &&
								(searchResult.state === "VALID" ||
									searchResult.state === "DRAFT_OTHER_OWNING_DEALERSHIP") && (
									<PButton
										loading={isCreatingDraftVehicle}
										onClick={create}
										data-testid="add-vehicle-modal_add-vehicle-button"
									>
										<FormattedMessage {...messages.foundButton} />
									</PButton>
								)}
							{searchResult?.vehicleId &&
								searchResult.state === "DRAFT" &&
								routes?.inventoryList?.vehicleDetails?.add && (
									<PLink icon="none">
										<Link
											to={routes.inventoryList.vehicleDetails.add.url(searchResult.vehicleId)}
											onClick={hide}
											data-testid="add-vehicle-modal_add-vehicle-button"
										>
											<FormattedMessage {...messages.foundButton} />
										</Link>
									</PLink>
								)}
							{searchResult?.vehicleId &&
								searchResult.state === "ALREADY_EXISTS" &&
								routes?.inventoryList?.vehicleDetails && (
									<PLink icon="none">
										<Link
											to={routes.inventoryList.vehicleDetails.url(searchResult.vehicleId)}
											onClick={hide}
											data-testid="add-vehicle-modal_view-vehicle-button"
										>
											<FormattedMessage {...messages.viewButton} />
										</Link>
									</PLink>
								)}
							<PButton type="button" variant="secondary" onClick={hide}>
								<FormattedMessage {...formMessages.cancel} />
							</PButton>
						</ButtonGroup>
					</>
				)}
			</ContentWrapper>
		</Modal>
	)
})

const ContentWrapper = styled.div`
	min-width: 40vw;
	max-width: 550px;
	min-height: 380px;
`
